import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import React from 'react';

import { AQUAMARINE, AQUAMARINE_SOFT } from '../../../theme';
import { FieldName } from './FieldNames';

export const Section: React.FC<{
  title: string;
  hint?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, hint, children }) => (
  <Stack spacing={1.5}>
    <Box>
      <Typography variant="subtitle1" fontWeight={600}>
        {title}
      </Typography>
      {hint && (
        <Typography variant="body2" color="text.disabled">
          {hint}
        </Typography>
      )}
    </Box>
    {children}
  </Stack>
);

/** A switch with a description, for one client setting. */
export const SettingSwitch: React.FC<{
  label: string;
  description?: string;
  /** Shown with "Show field names", e.g. clients.strictAppCheck. */
  fieldName: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}> = ({ label, description, fieldName, checked, onChange }) => (
  <Box>
    <FormControlLabel
      control={
        <Switch
          checked={checked}
          onChange={(event) => onChange(event.target.checked)}
        />
      }
      label={label}
    />
    <Box sx={{ pl: 6.5, mt: -0.5 }}>
      {description && (
        <Typography variant="body2" color="text.disabled">
          {description}
        </Typography>
      )}
      <FieldName>{fieldName}</FieldName>
    </Box>
  </Box>
);

/** Rarely used settings, collapsed unless one of them is in use. */
export const CollapsedSettings: React.FC<{
  title: string;
  description: string;
  /** How many settings are in use, shown on the header. */
  inUse: number;
  inUseLabel: string;
  noneLabel: string;
  /** Opens it, e.g. when a setting inside needs attention. */
  forceOpen?: boolean;
  children: React.ReactNode;
}> = ({
  title,
  description,
  inUse,
  inUseLabel,
  noneLabel,
  forceOpen = false,
  children,
}) => {
  const [open, setOpen] = React.useState(inUse > 0);
  return (
    <Accordion
      variant="outlined"
      disableGutters
      expanded={open || forceOpen}
      onChange={(_, expanded) => setOpen(expanded)}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          sx={{ width: '100%', pr: 1 }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography fontWeight={600}>{title}</Typography>
            <Typography variant="body2" color="text.disabled">
              {description}
            </Typography>
          </Box>
          <Chip
            size="small"
            label={inUse ? `${inUse} ${inUseLabel}` : noneLabel}
            color={inUse ? 'primary' : 'default'}
            variant={inUse ? 'filled' : 'outlined'}
          />
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={2}>{children}</Stack>
      </AccordionDetails>
    </Accordion>
  );
};

/** Parses a whole-number field; blank and invalid input are null. */
export const parseWholeNumber = (value: string) => {
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) {
    return null;
  }
  return Number(trimmed);
};

/** Side by side when there's room, stacked on narrow screens. */
export const ChoiceCards: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Box
    sx={{
      display: 'grid',
      gap: 1.25,
      gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    }}
  >
    {children}
  </Box>
);

/** One option of a choice: a checkbox or radio with a title and description. */
export const ChoiceCard: React.FC<{
  control: React.ReactElement;
  checked: boolean;
  title: string;
  tag?: { label: string; recommended: boolean };
  description: string;
  fieldName?: string;
  /** The radio value, inside a RadioGroup. */
  value?: string;
}> = ({ control, checked, title, tag, description, fieldName, value }) => (
  <FormControlLabel
    value={value}
    control={control}
    sx={{
      m: 0,
      p: 1.5,
      gap: 0.5,
      alignItems: 'flex-start',
      border: 1,
      borderColor: checked ? AQUAMARINE : 'divider',
      borderRadius: 2,
      bgcolor: checked ? AQUAMARINE_SOFT : undefined,
      '& .MuiFormControlLabel-label': { minWidth: 0 },
      '& > .MuiButtonBase-root': { p: 0, mt: 0.25 },
    }}
    label={
      <Box>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
        >
          <Typography variant="body2" fontWeight={600}>
            {title}
          </Typography>
          {tag && (
            <Chip
              size="small"
              label={tag.label}
              color={tag.recommended ? 'primary' : 'default'}
              variant={tag.recommended ? 'filled' : 'outlined'}
              // A compact pill whose label wraps rather than truncating on
              // a narrow card.
              sx={{
                height: 'auto',
                maxWidth: '100%',
                fontSize: 11,
                fontWeight: 600,
                '& .MuiChip-label': {
                  px: 0.875,
                  py: 0.375,
                  whiteSpace: 'normal',
                },
              }}
            />
          )}
        </Stack>
        <Typography variant="caption" component="p" color="text.disabled">
          {description}
        </Typography>
        {fieldName && <FieldName>{fieldName}</FieldName>}
      </Box>
    }
  />
);
